import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";
import { AxiosRequest } from "../ApiCall";
import { configEnv } from "../constant";

export type SchoolCategory = "KV" | "JNV" | "KGBV" | "DEF" | "PVT" | "GOVT";
export type SchoolMedium = "English" | "Hindi" | "Regional";
export type SchoolStatus = "active" | "inactive" | "archived";
export type AssessmentWave = "BL" | "EL";
export type AssessmentLinkStatus = "draft" | "active" | "expired";

export interface SchoolProfile {
    schoolId: string;
    category: SchoolCategory;
    medium: SchoolMedium;
    stateCode: string;
    districtCode: string;
    sequence: number;
    status: SchoolStatus;
}

export interface OrgClassDef {
    name: string;
    sections: string[];
}

export interface SubOrganization {
    _id: string;
    name: string;
    location?: string;
    type?: string;
    parentOrganizationId?: string;
    isMasterOrganization: boolean;
    schoolProfile?: SchoolProfile;
    classes?: OrgClassDef[];
    createdAt?: string;
    updatedAt?: string;
}

export interface SchoolDashboardStats {
    scope: {
        rootOrganizationId: string;
        includesDescendants: boolean;
        descendantCount: number;
    };
    students: { total: number; byWave: { BL: number; EL: number } };
    attempts: {
        inProgress: number;
        submitted: number;
        graded: number;
        completionRate: number;
        averageScore: number | null;
    };
    links: { active: number; draft: number; expired: number };
    classBreakdown: {
        className: string;
        sections: { name: string; registered: number; submitted: number; avgScore: number | null }[];
        registered: number;
        submitted: number;
        avgScore: number | null;
    }[];
}

export interface AssessmentLink {
    _id: string;
    organization: string;
    schoolId: string;
    evaluationId: string;
    assessmentId: string;
    wave: AssessmentWave;
    year: number;
    status: AssessmentLinkStatus;
    windowOpensAt?: string;
    windowClosesAt?: string;
    url: string;
    createdAt?: string;
}

/**
 * Standard wrapper that surfaces the server's error message via a toast and rethrows
 * so callers can handle UI state (set loading false, close modal, etc.) cleanly.
 */
async function call<T>(
    promise: Promise<any>,
    successToast?: { title: string; severity?: NotificationSeverity }
): Promise<T> {
    try {
        const resp = await promise;
        if (successToast) {
            minimalToast({
                title: successToast.title,
                message: resp.data?.message || "Success",
                type: successToast.severity || NotificationSeverity.SUCCESS,
                category: NotificationCategory.UPDATE,
                priority: NotificationPriority.MEDIUM,
            });
        }
        return resp.data as T;
    } catch (error: any) {
        const msg =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "Request failed";
        minimalToast({
            title: "Error",
            message: msg,
            type: NotificationSeverity.ERROR,
            category: NotificationCategory.ALERT,
            priority: NotificationPriority.HIGH,
        });
        throw error;
    }
}

// Shared cache for listChildren: multiple components on one page fetch the
// same children list on mount; within the TTL they share a single request.
// Client-side and tiny (a few entries per browser tab). Freshness is
// event-driven, not just time-driven: every mutation that can change the
// children list calls invalidateChildrenCache() below, so a newly added or
// archived school is visible on the very next read.
const childrenListCache = new Map<string, { at: number; promise: Promise<any> }>();
const CHILDREN_CACHE_TTL_MS = 30_000;

export function invalidateChildrenCache(orgId?: string) {
    if (!orgId) {
        childrenListCache.clear();
        return;
    }
    for (const key of childrenListCache.keys()) {
        if (key.startsWith(`${orgId}|`)) childrenListCache.delete(key);
    }
}

export const schoolFlowApi = {
    // ----- Hierarchy -----
    toggleMasterFlag: (orgId: string, isMasterOrganization: boolean, header: any) =>
        call(
            AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${orgId}/master-flag`,
                { isMasterOrganization },
                header
            ),
            { title: "Master organization flag" }
        ),

    createSubOrganization: (
        parentId: string,
        body: {
            name: string;
            category: SchoolCategory;
            medium: SchoolMedium;
            stateCode: string;
            districtCode: string;
            type?: string;
            location?: string;
        },
        header: any
    ) =>
        call<{ data: SubOrganization; message: string }>(
            AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/organization/${parentId}/sub-organizations`,
                body,
                header
            ),
            { title: "School created" }
        ).then((r) => {
            invalidateChildrenCache(parentId);
            return r;
        }),

    bulkCreateSubOrganizations: (
        parentId: string,
        schools: Array<{
            name: string;
            category: SchoolCategory;
            medium: SchoolMedium;
            stateCode: string;
            districtCode: string;
            classes?: { name: string; sections: string[] }[];
        }>,
        header: any
    ) =>
        call<{
            data: {
                createdCount: number;
                failedCount: number;
                created: SubOrganization[];
                errors: { index: number; row: any; error: string }[];
            };
        }>(
            AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/organization/${parentId}/sub-organizations/bulk`,
                { schools },
                header
            ),
            { title: "Bulk upload complete" }
        ).then((r) => {
            invalidateChildrenCache(parentId);
            return r;
        }),

    listChildren: (
        orgId: string,
        header: any,
        status?: SchoolStatus,
        options?: { picker?: boolean; hydrate?: boolean }
    ) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (options?.hydrate) {
            params.set("picker", "false");
        } else if (options?.picker !== false) {
            params.set("picker", "true");
        }
        const query = params.toString();
        // Several components on the same page (management header, share
        // settings, data exchange) each fetch the children list on mount.
        // Share one in-flight request / recent result per orgId+query so a
        // single page load hits the network once.
        const cacheKey = `${orgId}|${query}`;
        const cached = childrenListCache.get(cacheKey);
        if (cached && Date.now() - cached.at < CHILDREN_CACHE_TTL_MS) {
            return cached.promise;
        }
        const promise = call<{ data: SubOrganization[] }>(
            AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/${orgId}/children${
                    query ? `?${query}` : ""
                }`,
                null,
                header
            )
        ).catch((err: any) => {
            // Never cache failures.
            childrenListCache.delete(cacheKey);
            throw err;
        });
        childrenListCache.set(cacheKey, { at: Date.now(), promise });
        return promise;
    },

    getDescendants: (orgId: string, header: any) =>
        call<{ data: { rootId: string; descendantIds: string[] } }>(
            AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/${orgId}/descendants`,
                null,
                header
            )
        ),

    getSchoolDashboard: (orgId: string, includeDescendants: boolean, header: any) =>
        call<{ data: SchoolDashboardStats }>(
            AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/${orgId}/school-dashboard?includeDescendants=${includeDescendants}`,
                null,
                header
            )
        ),

    manualAddStudent: (
        orgId: string,
        body: {
            name: string;
            gender?: "Male" | "Female" | "Other";
            class: string;
            section: string;
            rollNumber: string | number;
            wave?: AssessmentWave;
            academicYear?: number;
        },
        header: any
    ) =>
        call<{ data: any }>(
            AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/organization/${orgId}/students/manual`,
                body,
                header
            ),
            { title: "Student added" }
        ),

    listSchoolStudents: (orgId: string, header: any) =>
        call<{ data: any[] }>(
            AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/${orgId}/students/school`,
                null,
                header
            )
        ),

    updateSchoolStatus: (orgId: string, status: SchoolStatus, header: any) =>
        call(
            AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${orgId}/school-status`,
                { status },
                header
            ),
            { title: "School status updated" }
        ).then((r) => {
            // orgId here is the school, not its parent — clear everything
            // (the cache holds a handful of entries at most).
            invalidateChildrenCache();
            return r;
        }),

    previewSchoolId: (
        params: {
            parentId: string;
            category: SchoolCategory;
            stateCode: string;
            districtCode: string;
        },
        header: any
    ) =>
        call<{ data: { schoolId: string; nextSequence: number; mode?: string } }>(
            AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/preview-school-id?parentId=${encodeURIComponent(params.parentId)}&category=${params.category}&stateCode=${params.stateCode}&districtCode=${params.districtCode}`,
                null,
                header
            )
        ),

    // ----- Classes & Sections -----
    addClass: (orgId: string, body: { name: string; sections: string[] }, header: any) =>
        call(
            AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/organization/${orgId}/classes`,
                body,
                header
            ),
            { title: "Class added" }
        ),

    updateClass: (
        orgId: string,
        className: string,
        body: { sections?: string[]; newName?: string },
        header: any
    ) =>
        call(
            AxiosRequest(
                "PUT",
                `${configEnv.baseUrl}/organization/${orgId}/classes/${encodeURIComponent(
                    className
                )}`,
                body,
                header
            ),
            { title: "Class updated" }
        ),

    deleteClass: (orgId: string, className: string, header: any) =>
        call(
            AxiosRequest(
                "DELETE",
                `${configEnv.baseUrl}/organization/${orgId}/classes/${encodeURIComponent(
                    className
                )}`,
                null,
                header
            ),
            { title: "Class deleted" }
        ),

    // ----- Assessment Links -----
    createAssessmentLink: (
        orgId: string,
        body: {
            evaluationId: string;
            wave: AssessmentWave;
            year: number;
            assessmentId?: string;
            status?: AssessmentLinkStatus;
            windowOpensAt?: string;
            windowClosesAt?: string;
        },
        header: any
    ) =>
        call<{ data: AssessmentLink }>(
            AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/organization/${orgId}/assessment-links`,
                body,
                header
            ),
            { title: "Assessment link saved" }
        ),

    listAssessmentLinks: (orgId: string, header: any) =>
        call<{ data: AssessmentLink[] }>(
            AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/organization/${orgId}/assessment-links`,
                null,
                header
            )
        ),

    updateAssessmentLink: (
        orgId: string,
        linkId: string,
        body: {
            status?: AssessmentLinkStatus;
            windowOpensAt?: string;
            windowClosesAt?: string;
        },
        header: any
    ) =>
        call<{ data: AssessmentLink }>(
            AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/organization/${orgId}/assessment-links/${linkId}`,
                body,
                header
            ),
            { title: "Assessment link updated" }
        ),
};

// ----- Geo data -----
export interface IndiaDistrict {
    code: string;
    name: string;
}

export interface IndiaState {
    code: string;
    name: string;
    isUnionTerritory?: boolean;
    districts: IndiaDistrict[];
}

let geoCachePromise: Promise<IndiaState[]> | null = null;

export async function fetchIndiaGeo(): Promise<IndiaState[]> {
    if (!geoCachePromise) {
        geoCachePromise = AxiosRequest("GET", `${configEnv.baseUrl}/geo/india`, null, {})
            .then((resp: any) => resp.data?.data as IndiaState[])
            .catch((err: any) => {
                geoCachePromise = null; // allow retry on error
                throw err;
            });
    }
    return geoCachePromise;
}

// ----- Public student-facing assessment endpoints (no auth header needed) -----
export const publicAssessApi = {
    resolveLink: (schoolId: string, wave: AssessmentWave, year: number) =>
        AxiosRequest(
            "GET",
            `${configEnv.baseUrl}/assess/wave/${wave}/${year}/${schoolId}`,
            null,
            {}
        ).then((r: any) => r.data),

    previewStudentId: (
        schoolId: string,
        wave: AssessmentWave,
        year: number,
        body: { class: string; section: string; roll: string | number }
    ) =>
        AxiosRequest(
            "POST",
            `${configEnv.baseUrl}/assess/wave/${wave}/${year}/${schoolId}/preview-student-id`,
            body,
            {}
        ).then((r: any) => r.data),

    register: (
        schoolId: string,
        wave: AssessmentWave,
        year: number,
        body: {
            class: string;
            section: string;
            roll: string | number;
            name?: string;
            gender?: string;
        },
        query?: { class?: string; section?: string }
    ) => {
        const qs = new URLSearchParams();
        if (query?.class) qs.set("class", query.class);
        if (query?.section) qs.set("section", query.section);
        const qsStr = qs.toString();
        return AxiosRequest(
            "POST",
            `${configEnv.baseUrl}/assess/wave/${wave}/${year}/${schoolId}/register${qsStr ? `?${qsStr}` : ""}`,
            body,
            {}
        ).then((r: any) => r.data);
    },

    /** File a resume request against an in-progress attempt (wave/year link shape). */
    requestResume: (
        schoolId: string,
        wave: AssessmentWave,
        year: number,
        body: {
            class: string;
            section: string;
            roll: string | number;
            name?: string;
            note?: string;
        },
        query?: { class?: string; section?: string }
    ) => {
        const qs = new URLSearchParams();
        if (query?.class) qs.set("class", query.class);
        if (query?.section) qs.set("section", query.section);
        const qsStr = qs.toString();
        return AxiosRequest(
            "POST",
            `${configEnv.baseUrl}/assess/wave/${wave}/${year}/${schoolId}/request-resume${qsStr ? `?${qsStr}` : ""}`,
            body,
            {}
        ).then((r: any) => r.data);
    },

    resolveSchoolAssessment: (schoolId: string, publicTestId: string) =>
        AxiosRequest("GET", `${configEnv.baseUrl}/assess/${schoolId}/test/${publicTestId}`, null, {})
            .then((r: any) => r.data),

    /** File a resume request against an in-progress attempt (standalone test link shape). */
    requestResumeSchoolAssessment: (
        schoolId: string,
        publicTestId: string,
        body: {
            class: string;
            section: string;
            roll: string | number;
            name?: string;
            note?: string;
        },
        query?: { class?: string; section?: string }
    ) => {
        const qs = new URLSearchParams();
        if (query?.class) qs.set("class", query.class);
        if (query?.section) qs.set("section", query.section);
        const qsStr = qs.toString();
        return AxiosRequest(
            "POST",
            `${configEnv.baseUrl}/assess/${schoolId}/test/${publicTestId}/request-resume${qsStr ? `?${qsStr}` : ""}`,
            body,
            {}
        ).then((r: any) => r.data);
    },

    registerSchoolAssessment: (
        schoolId: string,
        publicTestId: string,
        body: { class: string; section: string; roll: string | number } | { studentId: string }
    ) =>
        AxiosRequest(
            "POST",
            `${configEnv.baseUrl}/assess/${schoolId}/test/${publicTestId}/register`,
            body,
            {}
        ).then((r: any) => r.data),
};

// ----- Display helpers -----

export const SCHOOL_CATEGORY_LABELS: Record<SchoolCategory, string> = {
    KV: "KV - Kendriya Vidyalaya",
    JNV: "JNV - Jawahar Navodaya",
    KGBV: "KGBV - Kasturba Gandhi Balika Vidyalaya",
    DEF: "DEF - Defence",
    PVT: "PVT - Private",
    GOVT: "GOVT - Government",
};

export const SCHOOL_MEDIUM_OPTIONS: { value: SchoolMedium; label: string }[] = [
    { value: "English", label: "English" },
    { value: "Hindi", label: "Hindi" },
    { value: "Regional", label: "Regional language" },
];

/** Display "2025-26" from a numeric year like 2025. */
export function formatAcademicYear(year: number): string {
    const next = String((year + 1) % 100).padStart(2, "0");
    return `${year}–${next}`;
}
